/**
 * CatalogApi.ts — Façade API du domaine catalog (Phase D2b Round 1 + Phase D4c).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 * Aucun import React ni Capacitor. Seul couplage externe autorisé :
 *   @/integrations/supabase/client et @/integrations/supabase/types.
 *
 * Entités couvertes : products, categories, tenant_universes.
 *
 * Entités hors scope (absentes du schéma types.ts mai 2026) :
 *   product_variants, product_groups, menus, recipes.
 *
 * Invariants respectés :
 *   4.A.10 — tenant_id absent des inputs standard (RLS côté serveur).
 *             Exception documentée : paramètre tenantId optionnel dans
 *             listProducts/listCategories/createProduct/createCategory/archiveProduct
 *             pour opérations cross-tenant super_admin. RLS reste actif.
 *   4.A.15 — aucun champ PII loggé.
 *   4.B.2  — zéro import supabase dans src/components/ ou src/pages/.
 *   4.B.4  — 0 any, types stricts dérivés de Database['public']['Tables'].
 *
 * Soft-delete catalog :
 *   products : champ `active` (boolean) — archiveProduct() set active=false.
 *   categories : pas de champ active/archived en DB — deleteCategory() hard.
 *   tenant_universes : champ `is_active` — archiveUniverse() set is_active=false.
 *
 * Note d'architecture (invariant 4.A.10) :
 *   Pour les méthodes standard (sans tenantId) : RLS filtre automatiquement.
 *   Pour les méthodes cross-tenant admin (avec tenantId optionnel) : un filtre
 *   .eq('tenant_id', tenantId) est ajouté EN PLUS de RLS. La RLS doit autoriser
 *   la lecture cross-tenant pour les super_admin (policy get_my_role='super_admin').
 *   Pour les inserts cross-tenant admin, tenant_id est passé explicitement dans
 *   le payload (nécessite que la policy RLS autorise l'insert cross-tenant).
 */

import { supabase } from '@/integrations/supabase/client';
import { barcodeFilter } from '@/lib/productSearch';
import type { Database, Json, TablesInsert } from '@/integrations/supabase/types';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, notFoundError, validationError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import {
  createProductSchema,
  updateProductSchema,
  createCategorySchema,
  updateCategorySchema,
  createUniverseSchema,
  updateUniverseSchema,
} from './types';
import type {
  Product,
  Category,
  Universe,
  UniverseInsert,
  CreateProductInput,
  UpdateProductInput,
  CreateCategoryInput,
  UpdateCategoryInput,
  CreateUniverseInput,
  UpdateUniverseInput,
  ListProductsFilters,
  ListCategoriesFilters,
  ListUniversesFilters,
} from './types';

// ---------------------------------------------------------------------------
// Types internes (non-exportés)
// ---------------------------------------------------------------------------

type ProductRow = Database['public']['Tables']['products']['Row'];
type CategoryRow = Database['public']['Tables']['categories']['Row'];
type UniverseRow = Database['public']['Tables']['tenant_universes']['Row'];

// ---------------------------------------------------------------------------
// CatalogApi
// ---------------------------------------------------------------------------

export const CatalogApi = {
  // ── Products ──────────────────────────────────────────────────────────────

  /**
   * Retourne la liste des produits du tenant courant.
   *
   * RLS filtre automatiquement. Par défaut seuls les produits actifs (active = true).
   * Phase D4c : `filters.tenantId` optionnel pour cross-tenant super_admin.
   *
   * @param filters Filtres optionnels (categoryId, includeArchived, universeId, tenantId).
   */
  async listProducts(filters?: ListProductsFilters): Promise<Result<Product[], AppError>> {
    let query = supabase.from('products').select('*');

    if (!filters?.includeArchived) {
      query = query.eq('active', true);
    }

    if (filters?.categoryId !== undefined) {
      query = query.eq('category_id', filters.categoryId);
    }

    if (filters?.universeId !== undefined) {
      query = query.eq('universe_id', filters.universeId);
    }

    // Filtre cross-tenant admin (invariant 4.A.10 exception documentée)
    if (filters?.tenantId !== undefined) {
      query = query.eq('tenant_id', filters.tenantId);
    }

    // Ordre manuel d'abord (NULLS LAST), fallback alphabétique historique
    const { data, error } = await query
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('name', { ascending: true });

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.listProducts'));
    }

    return ok((data ?? []) as ProductRow[]);
  },

  /**
   * Retourne un produit par son identifiant.
   *
   * RLS garantit que seul un produit du tenant courant est accessible.
   *
   * @param id UUID du produit.
   */
  async getProductById(id: string): Promise<Result<Product, AppError>> {
    const { data, error } = await supabase.from('products').select('*').eq('id', id).single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.getProductById'));
    }

    if (!data) {
      return err(notFoundError('product', id));
    }

    return ok(data as ProductRow);
  },

  /**
   * Retourne un produit actif par code-barres, ou null si aucun ne correspond.
   *
   * Fallback caisse : quand un code scanné est absent de la liste en cache
   * (produit créé quelques secondes plus tôt depuis un autre écran ou un
   * autre appareil), on vérifie en base avant d'ouvrir le dialog de création.
   * RLS scope le tenant courant.
   *
   * @param barcode Code-barres exact (EAN fabricant ou code interne).
   */
  async getProductByBarcode(barcode: string): Promise<Result<Product | null, AppError>> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .or(barcodeFilter(barcode))
      .eq('active', true)
      .maybeSingle();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.getProductByBarcode'));
    }

    return ok((data as ProductRow | null) ?? null);
  },

  /**
   * Crée un produit pour le tenant courant.
   *
   * Input validé par Zod. Invariant 4.A.10 : tenant_id injecté via RLS/DEFAULT.
   * Phase D4c : paramètre `tenantId` optionnel pour insert cross-tenant super_admin.
   *
   * @param input    Données du produit.
   * @param tenantId UUID tenant cible (super_admin cross-tenant, optionnel).
   */
  async createProduct(
    input: CreateProductInput,
    tenantId?: string,
  ): Promise<Result<Product, AppError>> {
    const parsed = createProductSchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données produit invalides',
        ),
      );
    }

    // Cross-tenant admin : on enrichit le payload avec tenant_id cible.
    // allergens: Record<string,unknown> → Json : les valeurs viennent de Zod et sont JSON-safe.
    const parsedProduct = parsed.data;
    const insertPayload = tenantId
      ? {
          ...parsedProduct,
          tenant_id: tenantId,
          allergens: parsedProduct.allergens as Json | undefined,
        }
      : { ...parsedProduct, allergens: parsedProduct.allergens as Json | undefined };

    // cast : Zod valide que name est présent (required en DB) et que allergens est JSON-safe.
    const { data, error } = await supabase
      .from('products')
      .insert(insertPayload as TablesInsert<'products'>)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.createProduct'));
    }

    if (!data) {
      return err(notFoundError('product', 'new'));
    }

    return ok(data as ProductRow);
  },

  /**
   * Met à jour un produit existant.
   *
   * Input validé par Zod avant tout appel Supabase.
   * RLS garantit que seul un admin/manager peut modifier un produit de son tenant.
   *
   * @param id     UUID du produit à modifier.
   * @param input  Champs à mettre à jour (tous optionnels).
   */
  async updateProduct(id: string, input: UpdateProductInput): Promise<Result<Product, AppError>> {
    const parsed = updateProductSchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données produit invalides',
        ),
      );
    }

    const parsedUpdate = parsed.data as typeof parsed.data & {
      allergens?: { [key: string]: Json };
    };
    const { data, error } = await supabase
      .from('products')
      .update({ ...parsedUpdate, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.updateProduct'));
    }

    if (!data) {
      return err(notFoundError('product', id));
    }

    return ok(data as ProductRow);
  },

  /**
   * Archive un produit (soft-delete via active = false). JAMAIS de hard delete (NF525).
   *
   * Phase D4c : `tenantId` optionnel pour cross-tenant admin — filtre garantissant
   * l'appartenance au bon tenant.
   *
   * @param id       UUID du produit à archiver.
   * @param tenantId UUID tenant cible (super_admin cross-tenant, optionnel).
   */
  async archiveProduct(id: string, tenantId?: string): Promise<Result<Product, AppError>> {
    let query = supabase
      .from('products')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', id);

    // Filtre cross-tenant admin — garantit qu'on n'archive que le produit du bon tenant
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query.select('*').single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.archiveProduct'));
    }

    if (!data) {
      return err(notFoundError('product', id));
    }

    return ok(data as ProductRow);
  },

  /**
   * Supprime physiquement un produit (hard delete). RÉSERVÉ ADMIN cross-tenant.
   *
   * Utiliser UNIQUEMENT pour des produits qui n'ont jamais été vendus.
   * La FK sale_items.product_id → products.id protège en DB : si le produit
   * est référencé dans une vente, la contrainte rejette la suppression.
   *
   * NE PAS utiliser depuis la caisse ou un contexte tenant self-service.
   * Préférer archiveProduct() dans tous les autres cas (invariant 4.A.8).
   *
   * Phase D4c : call site TenantProductsPanel.tsx handleDelete — l'UX affiche
   * "Supprimer" avec hard delete, sémantique conservée tel quel (règle NE CHANGE
   * PAS la sémantique visible).
   *
   * @param id       UUID du produit à supprimer.
   * @param tenantId UUID tenant cible (super_admin cross-tenant, optionnel).
   */
  async deleteProductHard(id: string, tenantId?: string): Promise<Result<void, AppError>> {
    let query = supabase.from('products').delete().eq('id', id);

    // Filtre cross-tenant admin — garantit qu'on ne supprime que le produit du bon tenant
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }

    const { error } = await query;

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.deleteProductHard'));
    }

    return ok(undefined);
  },

  /**
   * Purge l'intégralité du catalogue d'un tenant (produits PUIS catégories).
   * RÉSERVÉ super_admin — suppression de données de démo (consumer AppSidebar
   * purgeDemoData, Strangler lot 1).
   *
   * Hard delete assumé : produits/catégories ne sont pas des tables fiscales
   * NF525. La FK sale_items.product_id protège en DB — si un produit a déjà
   * été vendu, la contrainte rejette la purge (comportement souhaité, le
   * tenant n'est alors pas un compte démo).
   *
   * Ordre impératif : products avant categories (FK products.category_id).
   *
   * @param tenantId UUID du tenant à purger.
   */
  async purgeByTenant(tenantId: string): Promise<Result<void, AppError>> {
    const { error: productsError } = await supabase
      .from('products')
      .delete()
      .eq('tenant_id', tenantId);

    if (productsError) {
      return err(appErrorFromSupabase(productsError, 'CatalogApi.purgeByTenant'));
    }

    const { error: categoriesError } = await supabase
      .from('categories')
      .delete()
      .eq('tenant_id', tenantId);

    if (categoriesError) {
      return err(appErrorFromSupabase(categoriesError, 'CatalogApi.purgeByTenant'));
    }

    return ok(undefined);
  },

  /**
   * Restaure un produit archivé (active = true).
   *
   * @param id UUID du produit à restaurer.
   */
  async restoreProduct(id: string): Promise<Result<Product, AppError>> {
    const { data, error } = await supabase
      .from('products')
      .update({ active: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.restoreProduct'));
    }

    if (!data) {
      return err(notFoundError('product', id));
    }

    return ok(data as ProductRow);
  },

  // ── Categories ────────────────────────────────────────────────────────────

  /**
   * Retourne la liste des catégories du tenant courant.
   *
   * RLS filtre automatiquement. Triées par sort_order croissant.
   * Phase D4c : `filters.tenantId` optionnel pour cross-tenant super_admin.
   *
   * @param filters Filtres optionnels (universeId, tenantId).
   */
  async listCategories(filters?: ListCategoriesFilters): Promise<Result<Category[], AppError>> {
    let query = supabase.from('categories').select('*');

    if (filters?.universeId !== undefined) {
      query = query.eq('universe_id', filters.universeId);
    }

    // Filtre cross-tenant admin (invariant 4.A.10 exception documentée)
    if (filters?.tenantId !== undefined) {
      query = query.eq('tenant_id', filters.tenantId);
    }

    const { data, error } = await query.order('sort_order', { ascending: true });

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.listCategories'));
    }

    return ok((data ?? []) as CategoryRow[]);
  },

  /**
   * Retourne une catégorie par son identifiant.
   *
   * RLS garantit que seule une catégorie du tenant courant est accessible.
   *
   * @param id UUID de la catégorie.
   */
  async getCategoryById(id: string): Promise<Result<Category, AppError>> {
    const { data, error } = await supabase.from('categories').select('*').eq('id', id).single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.getCategoryById'));
    }

    if (!data) {
      return err(notFoundError('category', id));
    }

    return ok(data as CategoryRow);
  },

  /**
   * Crée une catégorie pour le tenant courant.
   *
   * Consumer prioritaire : Caisse.tsx:1419. Input validé par Zod.
   * Invariant 4.A.10 : tenant_id injecté via RLS/DEFAULT.
   * Phase D4c : `tenantId` optionnel pour insert cross-tenant super_admin.
   *
   * @param input    Données de la catégorie.
   * @param tenantId UUID tenant cible (super_admin cross-tenant, optionnel).
   */
  async createCategory(
    input: CreateCategoryInput,
    tenantId?: string,
  ): Promise<Result<Category, AppError>> {
    const parsed = createCategorySchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données catégorie invalides',
        ),
      );
    }

    // Cross-tenant admin : on enrichit le payload avec tenant_id cible
    // cast : parsed.data narrowing échoue avec strictNullChecks:false
    const parsedCategory = parsed.data as CreateCategoryInput;
    const insertPayload = tenantId ? { ...parsedCategory, tenant_id: tenantId } : parsedCategory;

    // cast : Zod valide que name est présent (required en DB) au runtime.
    const { data, error } = await supabase
      .from('categories')
      .insert(insertPayload as TablesInsert<'categories'>)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.createCategory'));
    }

    if (!data) {
      return err(notFoundError('category', 'new'));
    }

    return ok(data as CategoryRow);
  },

  /**
   * Met à jour une catégorie existante.
   *
   * Input validé par Zod avant tout appel Supabase.
   * RLS garantit que seul un admin/manager peut modifier une catégorie de son tenant.
   *
   * @param id     UUID de la catégorie à modifier.
   * @param input  Champs à mettre à jour (tous optionnels).
   */
  async updateCategory(
    id: string,
    input: UpdateCategoryInput,
  ): Promise<Result<Category, AppError>> {
    const parsed = updateCategorySchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données catégorie invalides',
        ),
      );
    }

    const { data, error } = await supabase
      .from('categories')
      .update(parsed.data)
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.updateCategory'));
    }

    if (!data) {
      return err(notFoundError('category', id));
    }

    return ok(data as CategoryRow);
  },

  /**
   * Supprime une catégorie.
   *
   * Note : la table categories n'a pas de champ active/archived.
   * La suppression est physique (hard delete) car les catégories ne sont pas
   * référencées dans les tables fiscales NF525 (sales, sale_items, fiscal_events…).
   * Les produits liés ont category_id nullable → ils deviennent sans catégorie.
   *
   * Si la catégorie est référencée dans des produits actifs, la DB peut renvoyer
   * une contrainte FK selon la policy ON DELETE configurée. RLS s'applique côté serveur.
   *
   * @param id UUID de la catégorie à supprimer.
   */
  async deleteCategory(id: string): Promise<Result<void, AppError>> {
    const { error } = await supabase.from('categories').delete().eq('id', id);

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.deleteCategory'));
    }

    return ok(undefined);
  },

  // ── Universes (tenant_universes) ──────────────────────────────────────────

  /**
   * Retourne la liste des univers du tenant courant.
   *
   * RLS filtre automatiquement par tenant.
   * Par défaut, seuls les univers actifs (is_active = true) sont retournés.
   *
   * @param filters Filtres optionnels (includeInactive).
   */
  async listUniverses(filters?: ListUniversesFilters): Promise<Result<Universe[], AppError>> {
    let query = supabase.from('tenant_universes').select('*');

    if (!filters?.includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query.order('sort_order', { ascending: true });

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.listUniverses'));
    }

    return ok((data ?? []) as UniverseRow[]);
  },

  /**
   * Retourne un univers par son identifiant.
   *
   * @param id UUID de l'univers.
   */
  async getUniverseById(id: string): Promise<Result<Universe, AppError>> {
    const { data, error } = await supabase
      .from('tenant_universes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.getUniverseById'));
    }

    if (!data) {
      return err(notFoundError('universe', id));
    }

    return ok(data as UniverseRow);
  },

  /**
   * Crée un univers pour le tenant courant.
   *
   * Input validé par Zod avant tout appel Supabase.
   * Invariant 4.A.10 : tenant_id absent de l'input schema — requis en DB mais
   *   injecté via la session auth du caller (RLS DEFAULT).
   *
   * Note technique : tenant_id est Required dans TenantUniversesInsert. On ne
   *   le passe pas dans l'input de l'appelant, mais la contrainte DB sera
   *   satisfaite si la RLS policy utilise un DEFAULT = get_my_tenant_id().
   *   Si la policy ne le fournit pas, l'insert échouera côté serveur avec
   *   une contrainte NOT NULL — comportement attendu et géré.
   *
   * @param input Données de l'univers à créer.
   */
  async createUniverse(input: CreateUniverseInput): Promise<Result<Universe, AppError>> {
    const parsed = createUniverseSchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données univers invalides',
        ),
      );
    }

    const { data, error } = await supabase
      .from('tenant_universes')
      .insert(parsed.data as UniverseInsert)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.createUniverse'));
    }

    if (!data) {
      return err(notFoundError('universe', 'new'));
    }

    return ok(data as UniverseRow);
  },

  /**
   * Met à jour un univers existant.
   *
   * @param id     UUID de l'univers à modifier.
   * @param input  Champs à mettre à jour (tous optionnels).
   */
  async updateUniverse(
    id: string,
    input: UpdateUniverseInput,
  ): Promise<Result<Universe, AppError>> {
    const parsed = updateUniverseSchema.safeParse(input);

    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données univers invalides',
        ),
      );
    }

    const { data, error } = await supabase
      .from('tenant_universes')
      .update(parsed.data)
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.updateUniverse'));
    }

    if (!data) {
      return err(notFoundError('universe', id));
    }

    return ok(data as UniverseRow);
  },

  /**
   * Archive un univers (soft-delete via is_active = false).
   *
   * Un univers archivé n'est plus visible mais ses catégories et produits liés
   * restent intacts.
   *
   * @param id UUID de l'univers à archiver.
   */
  async archiveUniverse(id: string): Promise<Result<Universe, AppError>> {
    const { data, error } = await supabase
      .from('tenant_universes')
      .update({ is_active: false })
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'CatalogApi.archiveUniverse'));
    }

    if (!data) {
      return err(notFoundError('universe', id));
    }

    return ok(data as UniverseRow);
  },
} as const;
